/* @flow */

import {
  isFunction,
  isObject,
  isString,
  isUndefined,
  isDOMElement,
  unique
} from './coreutils';
import { logError } from './logger';
import { getTimeoutPromise } from './promiseutils';

/**
 * @description Browser Utility from EOW
 * @module utils/Browserutil
 * @requires utils/coreutils
 * @requires utils/logger
 */

/**
 * @class
 */
class _BrowserUtil {
  _ua: string;

  _config: Array<browserConfig>;

  _cache: browserConfig;

  _uaReal: string;

  constructor(ua?: string = navigator.userAgent) {
    this._uaReal = ua;
    this._ua = ua.toLowerCase();
    this._config = [
      {
        search: 'edge',
        name: 'Edge',
        sname: 'edge',
        versionPrefix: 'edge',
        version: ''
      },
      {
        search: 'opera',
        name: 'Opera',
        sname: 'opera',
        versionPrefix: 'opera',
        version: ''
      },
      {
        search: 'chrome',
        name: 'Chrome',
        sname: 'chrome',
        versionPrefix: 'chrome',
        version: ''
      },
      {
        search: 'firefox',
        name: 'Firefox',
        sname: 'ff',
        versionPrefix: 'firefox',
        version: ''
      },
      {
        search: 'safari',
        name: 'Safari',
        sname: 'safari',
        versionPrefix: 'version',
        version: ''
      },
      {
        search: 'msie',
        name: 'Internet Explorer',
        sname: 'ie',
        versionPrefix: 'msie',
        version: ''
      },
      {
        search: 'trident',
        name: 'Internet Explorer',
        sname: 'ie',
        versionPrefix: 'rv',
        version: ''
      }
    ];
    this._cache = this._getInfo();
  }

  _getInfo(): browserConfig {
    const info = this._getName();
    info.version = this._getVersion(info);
    return info;
  }

  _getName(): browserConfig {
    // set default value
    const defRet = {
      search: 'unknown',
      name: 'Unknown',
      sname: 'others',
      versionPrefix: '',
      version: ''
    };
    const index = this._config.findIndex(obj => this._ua.includes(obj.search));
    return index === -1 ? defRet : this._config[index];
  }

  _getVersion(info: browserConfig): string {
    let offset =
      this._ua.indexOf(info.versionPrefix) + info.versionPrefix.length + 1; // +1 is for '/' or space or ':' after prefix
    let ret = '';
    let ch = this._ua.charAt(offset);
    // eslint-disable-next-line no-restricted-syntax
    while (ch && ch !== ' ' && (!Number.isNaN(Number(ch)) || ch === '.')) {
      ret += ch;
      offset += 1;
      ch = this._ua.charAt(offset);
    }
    return ret.split('.').splice(0, 2).join('.');
  }

  /**
   * @description This function is used to get the name of the user's browser
   * @returns {string} - [Chrome, Safari, Internet Explorer, Opera, Firefox] or Unknown
   */
  getName(): string {
    return this._cache.name;
  }

  /**
   * @description short name of the browser
   * @returns {string} - [ff, chrome, safari, ie, opera]
   */
  getShortName(): string {
    return this._cache.sname;
  }

  /**
   * @description get browser version
   * @returns {string} - browser version
   */
  getVersion(): string {
    return this._cache.version;
  }

  /**
   * @description get browser major version
   * @returns {number} - Version Number
   */
  getMajorVersion(): number {
    return Number.parseInt(this._cache.version, 10);
  }

  /**
   * @description check browser version is ie 10 and below
   * @returns {boolean} - IE<9 or not
   */
  isOldIE(): boolean {
    return this.getShortName() === 'ie' && this.getMajorVersion() <= 10;
  }

  /**
   * Returns useragent from navigator API
   *
   * @returns {string} - useragent
   */
  getUserAgent(): string {
    return this._uaReal;
  }
}

// export singleton object as default for this module
const BrowserUtil: _BrowserUtil = new _BrowserUtil();
export { BrowserUtil, _BrowserUtil };

// flowlint unclear-type:off
/**
 * parse JSON ad response, take care of removing OAS tags
 * Need to check this during openx migration!!
 * https://github.com/rollup/rollup/wiki/Troubleshooting#avoiding-eval
 *
 * @param {window} [w] - window object
 * @param {string} [data] - data to be parsed
 * @returns {object} - Parsed JSON data
 */
export function parseJSON(w?: WindowProxy = window, data?: string = ''): any {
  // flowlint unclear-type:error
  if (!isString(data) || !data.length) {
    return {};
  }
  /* jshint evil: true */
  const b = data.indexOf('{');
  const e = data.lastIndexOf('}');
  let s = '';
  if (b !== -1 && e !== -1 && e > b) {
    s = data.slice(b, e + 1);
  }
  if (s.length) {
    try {
      if (typeof w.JSON === 'object' && isFunction(w.JSON.parse)) {
        return w.JSON.parse(s);
      }
      if (isFunction(w.jQuery) && isFunction(w.jQuery.parseJSON)) {
        return w.jQuery.parseJSON(s);
      }
      const eval2 = w.eval;
      return eval2(`(${s})`);
    } catch {
      /* continue regardless of error */
    }
  }
  return {};
}

/**
 * load a JS file asynchronously.
 * [c]2014 @scottjehl, Filament Group, Inc.
 * (Based on http://goo.gl/REQGQ by Paul Irish).
 * Licensed MIT
 * https://github.com/filamentgroup/loadJS/blob/master/loadJS.js
 *
 * @param {string} src - js file url
 * @param {Document} d - document object
 * @param {boolean} [bAsync=false] - async flag
 * @param {string} id - script tag id
 * @returns {HTMLScriptElement} - Newly created script DOM node
 */
export function loadJS(
  src: string,
  d: Document,
  bAsync?: boolean,
  id?: string = ''
): HTMLScriptElement | null {
  bAsync = bAsync || false;
  src = src || '';
  if (!src.length) {
    return null;
  }
  const script = d.createElement('script');
  script.src = src;
  if (id.length) {
    script.id = id;
  }
  if (bAsync) {
    script.async = true;
  }
  const { head } = d;
  if (head) {
    const headScripts = head.getElementsByTagName('script');
    if (headScripts.length > 0) {
      // eslint-disable-next-line unicorn/prefer-at
      const lastHeadScript = headScripts[headScripts.length - 1];
      // Insert after the last script tag in head
      if (lastHeadScript.nextSibling) {
        head.insertBefore(script, lastHeadScript.nextSibling);
      } else {
        head.appendChild(script);
      }
      return script;
    }
  }
  // Fallback: append to body if no scripts in head
  try {
    if (d.body) {
      d.body.appendChild(script);
    }
  } catch {
    /**/
  }
  return script;
}

/**
 * Promise wrapper for loadJS resolving on successful load and rejecting on error.
 *
 * @param {string} src - js file url
 * @param {Document} d - document object
 * @param {boolean} bAsync - async flag
 * @returns {Promise<void>} - resolves when the script loads, rejects on error
 */
export function loadJSPromise(
  src: string,
  d: Document,
  bAsync: boolean
): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = loadJS(src, d, bAsync);
    if (!script) {
      reject(`Script loading failed ${src}`);
      return;
    }
    /* eslint-disable unicorn/prefer-add-event-listener */
    script.onerror = () => {
      logError(`${src} script load error`);
      reject(`${src} script load error`);
    };
    // $FlowFixMe[missing-this-annot]
    script.onload = script.onreadystatechange = function () {
      // $FlowFixMe[prop-missing]
      if (
        !this.readyState ||
        this.readyState === 'loaded' ||
        this.readyState === 'complete'
      ) {
        script.onload = null;
        script.onreadystatechange = null;
        resolve();
      }
    };
    /* eslint-enable unicorn/prefer-add-event-listener */
  });
}

/**
 * @global
 * @typedef loadJSWithTimeoutParams
 * @type {object}
 * @property {object} [context=window] Window Context
 * @property {string} [function] Callback Function
 * @property {string} [object] Object
 */

/**
 * Loads js file asynchronously
 *
 * @param {string} src - js file url
 * @param {Document} d - document object
 * @param {boolean} bAsync - async flag
 * @param {number} timeout - timeout for loading
 * @param {loadJSWithTimeoutParams} [o] - object to check for context/function
 * @returns {Promise<*>} - Promise for loading JS with timeout
 */
export function loadJSWithTimeout(
  src: string,
  d: Document,
  bAsync: boolean,
  timeout: number,
  o?: { context?: WindowProxy, func?: string, object?: string }
): Promise<void> {
  const params = o || {};
  const context = params.context || window;
  const func = params.func || '';
  const object = params.object || '';

  const timeoutPromise = getTimeoutPromise(timeout, `${src} `);

  return Promise.race([loadJSPromise(src, d, bAsync), timeoutPromise]).then(
    () => {
      if (
        (func && !isFunction(context[func])) ||
        (object && !isObject(context[object]))
      ) {
        const err = `${src} script loading failed :context check failed`;
        logError(err);
        throw err;
      }
      //eslint-disable-next-line no-useless-return
      return;
    },
    error =>
      // propagate error
      {
        throw error;
      }
  );
}

/**
 * Loads one or more JS files with an overall timeout and optional load mode.
 * Uses async script tags and no per-script timeout. Completion is determined
 * by script load events. Failure occurs if any script fails to load or if
 * the overall timeout elapses.
 *
 * @param {string|string[]} scripts - js file url or list of urls
 * @param {Document} d - document object
 * @param {boolean} bAsync - async flag
 * @param {('parallel'|'sequential')} [mode='parallel'] - load mode
 * @param {number} [overallTimeoutMs=0] - overall timeout for loading all scripts (0 to disable)
 * @returns {Promise<void>} - resolves when all scripts loaded, rejects on any failure/timeout
 */
export function loadMultipleJSWithTimeout(
  scripts: string | Array<string>,
  d: Document,
  bAsync: boolean,
  mode?: 'parallel' | 'sequential' = 'parallel',
  overallTimeoutMs?: number = 0
): Promise<void> {
  // $FlowFixMe[unclear-type]
  const list = ([scripts] as any)
    .flat()
    .filter((u: string) => typeof u === 'string' && u.trim().length > 0)
    .map((u: string) => u.trim());

  const urls: Array<string> = unique(list);

  if (!urls.length) {
    return Promise.resolve();
  }

  const loaderPromise =
    mode === 'sequential'
      ? urls.reduce(
          (p, url) => p.then(() => loadJSPromise(url, d, bAsync)),
          Promise.resolve()
        )
      : Promise.allSettled(urls.map(url => loadJSPromise(url, d, bAsync))).then(
          results => {
            const failed = results.filter(r => r && r.status === 'rejected');
            if (failed.length) {
              throw new Error(`One or more scripts failed to load`);
            }
            // eslint-disable-next-line no-useless-return
            return;
          }
        );

  if (!overallTimeoutMs || overallTimeoutMs <= 0) {
    return loaderPromise;
  }

  const timeoutPromise = getTimeoutPromise(overallTimeoutMs, 'scripts:');

  return Promise.race([loaderPromise, timeoutPromise]);
}

/**
 * load a CSS file asynchronously.
 * [c]2014 @scottjehl, Filament Group, Inc.
 * Licensed MIT
 * https://github.com/filamentgroup/loadCSS/blob/master/loadCSS.js
 *
 * @param {string} href - link src url
 * @param {Document} d - document object
 * @param {Element} [before] - element before which link tag needs to be inserted
 * @param {string} [media=all] - media type
 * @returns {HTMLLinkElement} - Newly created link tag for loading CSS
 */
export function loadCSS(
  href: string,
  d: Document,
  before?: Element,
  media?: string = 'all'
): HTMLLinkElement {
  // Arguments explained:
  // `href` is the URL for your CSS file.
  // `before` optionally defines the element we'll use as a reference for injecting our <link>
  // By default, `before` uses the first <script> element in the page.
  // However, since the order in which stylesheets are referenced matters, you might need a more specific location in your document.
  // If so, pass a different reference element to the `before` argument and it'll insert before that instead
  // note: `insertBefore` is used instead of `appendChild`, for safety re: http://www.paulirish.com/2011/surefire-dom-element-insertion/
  const ss: HTMLLinkElement = d.createElement('link');
  const ref: Element = before || d.getElementsByTagName('script')[0];
  ss.rel = 'stylesheet';
  ss.href = href;
  // temporarily, set media to something non-matching to ensure it'll fetch without blocking render
  ss.media = 'only x';
  // inject link
  if (ref && ref.parentNode) {
    ref.parentNode.insertBefore(ss, ref);
  }
  // set media back to `all` so that the styleshet applies once it loads
  setTimeout(() => {
    ss.media = media;
  });
  return ss;
}

/**
 * Inject global CSS string into document. Creates or updates a single <style> node.
 * Insertion follows loadJS pattern: prefer head, fallback to body.
 * Removes the node when css is empty to avoid leaving an empty style tag.
 *
 * @param {Document} doc - document object
 * @param {string} id - id for the style element (e.g. 'asjs-global-css')
 * @param {string} css - CSS text to inject
 * @returns {HTMLStyleElement|null} - style element or null if removed/empty
 */
export function injectGlobalCSS(
  doc: Document,
  id: string,
  css: string
): HTMLElement | null {
  const tid = id && String(id).trim();
  if (!tid.length) {
    return null;
  }
  let el = doc.getElementById(tid);
  const isEmpty = !css || String(css).trim().length === 0;
  if (isEmpty) {
    if (el) {
      el.remove();
    }
    return null;
  }
  if (!el) {
    el = doc.createElement('style');
    el.id = tid;
    const { head } = doc;
    if (head) {
      head.appendChild(el);
    } else {
      try {
        if (doc.body) {
          doc.body.appendChild(el);
        }
      } catch {
        /**/
      }
    }
  }
  el.textContent = css;
  return el;
}

/**
 * Preload given resource
 *
 * @param {string} href - link src url
 * @param {Document} d - document object
 * @param {string} [as] -  resource type (script/image/style/document)
 * @param {string} [type] - media type
 * @param {boolean} [crossorigin=false] - how to handle cross-origin request
 * @param {boolean} [before] - element before which link tag needs to be inserted
 * @returns {HTMLLinkElement} - Newly created link tag for preload
 */
export function preload(
  href: string,
  d: Document,
  as?: string = '',
  type?: string = '',
  crossorigin?: boolean = false,
  before?: Element
): HTMLLinkElement {
  const ref = before || d.getElementsByTagName('script')[0];
  const link = d.createElement('link');
  link.href = href;
  link.rel = 'preload';
  if (as.length) {
    link.as = as;
  }
  if (type.length) {
    link.type = type;
  }
  if (crossorigin) {
    link.crossOrigin = 'use-credentials';
  }
  if (ref && ref.parentNode) {
    ref.parentNode.insertBefore(link, ref);
  }
  return link;
}

/**
 * Browser exclusion list support
 * We can exclude based on browser name, version, page name
 * Format: '<name>|<version>|<pagename>,...'
 * '*' to exlude all
 *
 * @example '*|*|write' - Will exclude compose page in all browser
 * @param {string} s - Exclusion list as string
 * @param {_BrowserUtil} [bu] - Browser Util object
 * @returns {Array<string>} - Get browser exclusion list as Array
 */
export function getBrowserExcludeList(
  s: string,
  bu: _BrowserUtil = BrowserUtil
): Array<string> {
  const bName = bu.getShortName();
  const bVer = bu.getMajorVersion();
  const excludeList = (s || '').toLowerCase().split(',');
  let i = 0;
  let tmp = '';
  let arr: Array<string> = [];
  const ret = [];
  // eslint-disable-next-line no-restricted-syntax
  for (; i < excludeList.length; i += 1) {
    tmp = excludeList[i];
    arr = tmp.split('|');
    if (arr.length === 3) {
      if (tmp.includes('*')) {
        arr[0] = arr[0] === '*' ? bName : arr[0];
        arr[1] = arr[1] === '*' ? String(bVer) : arr[1];
      }
      tmp = arr.join('|');
      ret.push(tmp.toLowerCase());
    }
  }
  return ret;
}

/**
 * Check if current page/browser combo is in exclude list
 *
 * @param {string} pname - pagename
 * @param {Array<string>} exclList - exclude list formulated using getBrowserExcludeList
 * @param {_BrowserUtil} [bu] - Browser Util object
 * @returns {boolean} - Browser matches with exclusion list (name/version)
 */
export function isBrowserExcluded(
  pname: string,
  exclList: Array<string>,
  bu: _BrowserUtil = BrowserUtil
): boolean {
  pname = pname || '';
  exclList = exclList || [];
  if (!pname.length || !exclList.length) {
    return false;
  }
  const prefix = `${bu.getShortName()}|${bu.getMajorVersion()}|`;
  const p1 = (prefix + pname).toLowerCase();
  const p2 = `${prefix}*`.toLowerCase();
  let bRet = false;
  bRet = exclList.includes(p1) || exclList.includes(p2);
  return bRet;
}

/**
 *Checks if the passed window is in iframe
 *
 * @returns {Array<number>} - Width & Height of the screen
 */
export function getViewportDimensions(): [number, number] {
  const win: WindowProxy = window.top;
  const docEl: HTMLElement = win.document.documentElement;
  let w: number = 0;
  let h: number = 0;
  // Compliant browser supports innerwidth
  if (!isUndefined(win.innerWidth)) {
    w = win.innerWidth;
    h = win.innerHeight;
  } else if (isDOMElement(docEl)) {
    // older browsers
    w = docEl.clientWidth;
    h = docEl.clientHeight;
  }

  return [w, h];
}

/**
 * Check and returns how much given element is
 * visible in viewport vertically
 *
 * @param {document} d - Document object
 * @param {string} divId - Element ID to check
 * @returns {number} - Percentage in viewport
 */
function percentageInVerticalViewPort(
  d: Document = window.document,
  divId: string = ''
): number {
  const ele = d.getElementById(divId);
  if (!isDOMElement(ele)) {
    return 0;
  }
  // $FlowFixMe[incompatible-use]
  const rect = ele.getBoundingClientRect();

  let percentInVerticalViewport = 100;
  const [, windowHeight] = getViewportDimensions();

  // When the ad is completely visibile within the both window top and bottom bounds OR when it crosses both top and bottom window bounds
  if (
    (rect.top >= 0 && rect.bottom <= windowHeight) ||
    (rect.top < 0 && rect.bottom > windowHeight)
  ) {
    percentInVerticalViewport = 100;
  } else if (rect.bottom > 0 && rect.top < 0) {
    // When the ad is partly visible at the top edge
    percentInVerticalViewport = (rect.bottom / rect.height) * 100;
  } else if (rect.top < windowHeight && rect.bottom > windowHeight) {
    // When the ad is partly visible at the bottom edge
    percentInVerticalViewport = ((windowHeight - rect.top) / rect.height) * 100;
  } else {
    // ad is completely not visible
    percentInVerticalViewport = 0;
  }
  return percentInVerticalViewport;
}

/**
 * Check and returns how much given element is
 * visible in viewport horizontally
 *
 * @param {document} d - Document object
 * @param {string} divId - Element ID to check
 * @returns {number} - Percentage in viewport
 */
function percentageInHorizontalViewPort(
  d: Document = window.document,
  divId: string = ''
): number {
  const ele = d.getElementById(divId);
  if (!isDOMElement(ele)) {
    return 0;
  }
  // $FlowFixMe[incompatible-use]
  const rect = ele.getBoundingClientRect();

  let percentInHorizontalViewport = 100;
  const [windowWidth] = getViewportDimensions();

  // When the ad is completely visibile within the both window left and right bounds OR when it crosses both left and right window bounds
  if (
    (rect.left >= 0 && rect.right <= windowWidth) ||
    (rect.left < 0 && rect.bottom > windowWidth)
  ) {
    percentInHorizontalViewport = 100;
  } else if (rect.right > 0 && rect.left < 0) {
    // When the ad position is partly visiile at the left edge
    percentInHorizontalViewport = (rect.right / rect.width) * 100;
  } else if (rect.left < windowWidth && rect.right > windowWidth) {
    // When the ad position is partly visiile at the right edge
    percentInHorizontalViewport =
      ((windowWidth - rect.left) / rect.width) * 100;
  } else {
    // ad is completely not visible
    percentInHorizontalViewport = 0;
  }
  return percentInHorizontalViewport;
}

/**
 * Returns the percentage of the element in the viewport
 * The element or its parent should not have any CSS property such as dispaly:none to make it invisible to user
 *
 * @param {Document} d - document object
 * @param {string} divId - Div id of the element
 * @returns {number} - Percentage of div in the viewport
 */
export function percentageInViewPort(
  d: Document = window.document,
  divId: string = ''
): number {
  if (!divId.length) {
    return 0;
  }

  const ele = d.getElementById(divId);
  if (!isDOMElement(ele)) {
    return 0;
  }

  // If getBoundingClientRect is not supported
  // $FlowFixMe[incompatible-use]
  if (!isFunction(ele.getBoundingClientRect)) {
    return 100;
  }

  return Math.min(
    percentageInVerticalViewPort(d, divId),
    percentageInHorizontalViewPort(d, divId)
  );
}
/**
 * Check browser suppport for native JSON processing
 *
 * @param {window} w - Window object
 * @returns {boolean} - True/False
 */
export function isJSONSupported(w: WindowProxy = window): boolean {
  return typeof w.JSON === 'object' && isFunction(w.JSON.stringify);
}

/**
 * Check browser suppport for finding effective type of connection
 * and returns the type or returns empty string
 *
 * @returns {string} - {'slow-2g', '2g', '3g', or '4g'}'
 */
export function getEffectiveType(): string {
  // $FlowFixMe[prop-missing]
  const { connection } = navigator;
  return connection === null || connection === undefined
    ? ''
    : (connection.effectiveType ?? '');
}

/**
 * Get effective type and decide if it is dialup or not,
 * if the effective type is not available return the value
 * passed in parameters
 *
 * @param {boolean} d - dialup value from publisher website
 * @returns {boolean} - True/False
 */
export function getEvaluatedDialup(d: boolean): boolean {
  const eType = getEffectiveType();
  if (eType !== '') {
    return eType === 'slow-2g' || eType === '2g';
  }
  return d;
}

/**
 * Returns true if the page is https or
 * Returns false if the page is http
 *
 * @returns {boolean} - true/false
 */
export function isSecure(): boolean {
  const loc: Location = window.location;
  return loc.protocol === 'https:';
}

/**
 * Utility function to get hostname
 *
 * @returns {string} Hostname
 */
export function getHostname(): string {
  const loc: Location = window.location;
  return loc.hostname;
}

/**
 * Get window object with fallback for test environments
 * In test environments, window might not be in module scope when loaded via proxyquire,
 * so fallback to global.window
 * @returns {Window|null} Window object or null if not available
 */
export function getWindow(): anyObject | null {
  if (typeof window !== 'undefined') {
    return window;
  }
  // Fallback for test environments where window might be on global
  if (typeof global !== 'undefined' && global.window) {
    return global.window;
  }
  return null;
}

/**
 * Get location.href with fallback for test environments
 * Uses window.location.href (standard) with proper fallback handling
 * @returns {string} Current page URL or empty string if not available
 */
export function getLocationHref(): string {
  const win = getWindow();
  return win?.location?.href || '';
}

/**
 * @returns {string} Current page URL
 */
export function getPageUrl(): string {
  return getLocationHref();
}

/**
 * @returns {string} real referrer for current page
 */
export function getReferrer(): string {
  return document.referrer || '';
}

/**
 * Get canonical URL from <link rel="canonical"> tag or fallback to origin
 * @param {Document} [doc] - Document object, defaults to window.document
 * @returns {string} Canonical URL or origin URL (protocol://host/), empty string if unavailable
 */
export function getCanonicalUrl(doc?: Document = window.document): string {
  // Default: extract origin from page URL (location API works in IE6+)
  let canonicalUrl = '';
  const loc = getWindow()?.location;
  if (loc?.protocol && loc?.host) {
    canonicalUrl = `${loc.protocol}//${loc.host}/`;
  }

  // Override with canonical link if available
  try {
    const canonicalLink = doc.querySelector("link[rel='canonical']");
    // $FlowFixMe[prop-missing] - HTMLLinkElement has href property
    if (canonicalLink !== null && canonicalLink !== undefined) {
      // $FlowFixMe[prop-missing] - HTMLLinkElement has href property
      const { href } = canonicalLink;
      if (typeof href === 'string' && href.length > 0) {
        canonicalUrl = href;
      }
    }
  } catch {
    // If querySelector fails, return the default origin
  }

  return canonicalUrl;
}

/**
 * @returns {string} language from navigator or empty string
 */
export function getLanguage(): string {
  // navigator.language may be undefined in some environments
  // $FlowFixMe[prop-missing]
  return (typeof navigator !== 'undefined' && navigator.language) || '';
}

/**
 * Returns OpenRTB 2.6 compatible User-Agent Client Hints (device.sua) when available
 * Synchronous best-effort (uses low-entropy hints only)
 *
 * @returns {anyObject|null} - device.sua object or null if not supported
 */
export function getClientHintsUA(): anyObject | null {
  // $FlowFixMe[prop-missing]
  const uad = navigator && navigator.userAgentData;
  if (!uad) {
    return null;
  }
  // brands: [{brand, version}], mobile: boolean, platform: string (may be low-entropy)
  const brands = Array.isArray(uad.brands) ? uad.brands : [];
  const browsers = brands.map(b => ({
    brand: b.brand,
    version: [String(b.version)]
  }));
  const platformBrand = typeof uad.platform === 'string' ? uad.platform : '';
  return {
    source: 1,
    mobile: uad.mobile ? 1 : 0,
    platform: { brand: platformBrand },
    browsers
  };
}

/**
 * Returns true if mobile is supported or
 * Returns false if mobile is not supported
 *
 * @returns {boolean} - true/false
 */
export function isMobile(): boolean {
  return BrowserUtil.getUserAgent().includes('Mobi');
}

/**
 * Returns true for no tracking or
 * Returns false for unrestricted tracking
 *
 * @returns {boolean} - true/false
 */
export function isDNT(): boolean {
  const dnt: string | number = navigator.doNotTrack ?? window.doNotTrack ?? '';
  return dnt === 1 || dnt === '1' || dnt === 'yes';
}

/**
 * Returns true if Global Privacy Control (GPC) is enabled
 * GPC indicates user does not consent to sell or share their personal information
 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/globalPrivacyControl
 *
 * @returns {boolean} - true if GPC is enabled, false otherwise
 */
export function isGPC(): boolean {
  // $FlowFixMe[prop-missing]
  return navigator.globalPrivacyControl === true;
}
/**
 * Check whether DOM element support given feature or not
 *
 * @param {DOMTokenList} tokenList - List of features supported by DOM
 * @param {string} token - Feature to check
 * @returns {boolean} - Feature supported?
 */
function DOMTokenListSupports(tokenList: DOMTokenList, token: string): boolean {
  try {
    return Boolean(tokenList) && tokenList.supports(token);
  } catch {
    /**/
  }
  return false;
}

/**
 * checks if link preload is supported in the browser
 *
 * @param {Document} d - Document DOM Node
 * @returns {boolean} - True/False
 */
function isLinkPreloadSupported(d: Document = document): boolean {
  // In test environments (jsdom), document may be unset at module load time.
  const doc = d || (typeof document === 'undefined' ? null : document);
  if (!doc) {
    return false;
  }
  const el = doc.createElement('link');
  if (!el) {
    return false;
  }
  // $FlowFixMe[prop-missing]
  return DOMTokenListSupports(el.relList, 'preload');
}

// don't repeat the check again and again to avoid creating link tags unnecessarily
// Function already handles document availability checks internally
const isPreloadSupported = isLinkPreloadSupported();

/**
 * Generic function to preload any resources on supported browser
 *
 * @param {Document} d - document object
 * @param {string} src - url of the resource to be preloaded
 * @param {string} as - type
 * @returns {undefined}
 */
export function preloadResource(
  d: Document = document,
  src: string = '',
  as: string = ''
) {
  if (!d || !src.length || !as.length || !isPreloadSupported) {
    return;
  }

  // link preload supported
  preload(src, d, as);
}
/**
 * attach event to given element
 *
 * @param {Element} elem - DOM element
 * @param {string} type - event type
 * @param {function()} cB - event callback function
 * @returns {undefined}
 */
export function addEvent(elem: HTMLElement, type: string, cB: cbFunc) {
  // $FlowFixMe[method-unbinding]
  if (elem && elem.addEventListener) {
    elem.addEventListener(type, cB, false);
  } else if (elem && elem.attachEvent) {
    elem.attachEvent(`on${type}`, cB);
  }
}

/**
 * remove event attached to an element
 *
 * @param {Element} elem - DOM element
 * @param {string} type - event type
 * @param {function()} cB - event callback function
 * @returns {undefined}
 */
export function removeEvent(elem: HTMLElement, type: string, cB: cbFunc) {
  // $FlowFixMe[method-unbinding]
  if (elem && elem.removeEventListener) {
    elem.removeEventListener(type, cB, false);
  } else if (elem && elem.detachEvent) {
    elem.detachEvent(`on${type}`, cB);
  }
}

/**
 * Send tracking pixel request using navigator.sendBeacon
 * in modern browsers
 *
 * @param {string} url beacon request url
 * @returns {undefined}
 */
export function sendBeacon(url: string): boolean {
  try {
    // $FlowFixMe[incompatible-type]
    return navigator && navigator.sendBeacon && navigator.sendBeacon(url);
  } catch {
    logError(`sendBeacon failed with error`);
  }
  return false;
}

/**
 *Checks if the passed window is in iframe
 *
 * @param {window} [w] - window object
 * @returns {boolean} - If the passed window is in an iframe
 */
export function inIframe(w?: WindowProxy = window): boolean {
  return w.self && w.top && w.self !== w.top;
}

/**
 *
 * @param {string} str Argument
 * @returns {string} with Title case
 */
function camelCaseToTitleCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const vendorPrefixes = ['Webkit', 'webkit', 'Moz', 'moz', 'ms', 'O', 'o'];

/**
 * Get supported vendor prefix for the given property
 *
 * @param {Document} doc Document object
 * @param {string} prop Property to check
 * @returns {string} Vendor prefix if applicable
 */
export function getSupportedVendorPrefix(doc: Document, prop: string): string {
  if (!isString(prop) || prop.length < 1) {
    return '';
  }
  // Check if property is supported
  // $FlowFixMe[prop-missing]
  if (!isUndefined(doc[prop])) {
    return '';
  }

  // get title case of property hidden => Hidden
  const modifiedProp = camelCaseToTitleCase(prop);
  const found = vendorPrefixes.findIndex(
    // $FlowFixMe[prop-missing]
    prefix => !isUndefined(doc[`${prefix}${modifiedProp}`])
  );
  if (found !== -1) {
    return vendorPrefixes[found];
  }
  return '';
}
/**
 *
 * utility to get vendor prefixed supported prob
 *
 * @param {Document} doc Window document
 * @param {string} prop Property to get with vendor prefix
 * @returns {string} Vendor prefix-ed & supported property
 */
export function getVendorJsPropertyName(doc: Document, prop: string): string {
  if (!isString(prop) || prop.length < 1) {
    return '';
  }

  return `${getSupportedVendorPrefix(doc, prop)}${prop}`;
}

// Use window.location.hostname (standard) - in test environments, window might not be
// in module scope when loaded via proxyquire, so fallback to global.window
// Reference: window.location is the standard way to access location

const win = getWindow();
const uolenv =
  win && win.location && win.location.hostname
    ? /\.((qa|stg)\d)/.exec(win.location.hostname)
    : null;

/**
 * Utility to get uolenv.
 *
 *
 * @returns {string} - uolenv
 */
export function getuolenv(): anyObject {
  if (uolenv && uolenv[1]) {
    return { uolenv: uolenv[1] };
  }
  return {};
}

/**
 * Utility to parse JSON.
 *
 * @param {string} str - JSON string
 * @param {string} key - file name
 * @returns {anyObject|null} - Parsed JSON object or null if parsing fails
 */
export function JSONParse(str: string, key: string): anyObject | null {
  try {
    return JSON.parse(str);
  } catch (error) {
    logError(
      `[JSONParseError][${key}] failed to parse JSON. Input:${str}. Error: ${error}`
    );
    return null;
  }
}
